// /stores/index.ts
import { inject, IWrappedComponent } from "mobx-react";
import { FlexViewModelStore } from "./flexViewModel/FlexViewModelStore";
import { MarkdownStore } from "./markdownHandler/typings";
import { TemplateStore } from "./TemplateStore";
import { AnalyticsStore, RouteStore } from "bernie-core";
import { PoiStore } from "./poiStore/PoiStore";
import { ThemeSelectorStore } from "./ThemeSelectorStore";
import { ShortlistStore } from "./ShortlistStore";
import { UIPageStore } from "./UIPageStore";
import { CookiesStore } from "./CookiesStore";
import { ThumborStore } from "./thumbor/ThumborStore";
import { HotelsStore } from "./HotelsStore";
import { RecentlyViewedStore } from "./RecentlyViewedStore";
import { StaticMapStore } from "./staticMap/StaticMapStore";
import { DXStore } from "./DXStore";
import { AdvertisingStore } from "./AdvertisingStore";
import { DestinationSuggestionsStore } from "./DestinationSuggestionsStore";
import { CompositionStore } from "./CompositionStore";
import { WizardStore } from "./wizard/WizardStore";
import { MerchCampaignsStore } from "./MerchCampaignsStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { DealsStore } from "./DealsStore";
import { PackagePillStore } from "./PackagePillStore";
import { FlexModuleModelStore } from "./FlexModuleModelStore";
import { BlossomComponentTreeStore } from "./BlossomComponentTree/BlossomComponentTreeStore";

// Add to this list whenever a new store is added.
export interface StoresType {
  analytics: AnalyticsStore;
  routeStore: RouteStore;
  flexViewModel: FlexViewModelStore;
  templateStore: TemplateStore;
  shortlist: ShortlistStore;
  uiPage: UIPageStore;
  cookies: CookiesStore;
  thumbor: ThumborStore;
  markdown: MarkdownStore;
  hotels: HotelsStore;
  recentlyViewed: RecentlyViewedStore;
  staticMap: StaticMapStore;
  dx: DXStore;
  advertising: AdvertisingStore;
  destinationSuggestions: DestinationSuggestionsStore;
  wizardState: WizardStore;
  poi: PoiStore;
  compositionStore: CompositionStore;
  themeSelectorStore: ThemeSelectorStore;
  merchCampaigns: MerchCampaignsStore;
  context: ExtendedContextStore;
  dealsStore: DealsStore;
  packagePillStore: PackagePillStore;
  flexModuleModelStore: FlexModuleModelStore;
  blossomComponentTreeStore: BlossomComponentTreeStore;
}

/**
 * Use this instead of {@code @inject} so you will not have to provide stores in
 * tests or use the non-null assertion operator (!) and ensure strong typing.
 *
 * @example
 * interface ExampleProps { name: string; analytics: AnalyticsStore; }
 * const Example = withStores("analytics") = (props: ExampleProps) => {...}
 */
export const withStores = <TStoreProps extends keyof StoresType>(...stores: TStoreProps[]) => <
  TProps extends Pick<StoresType, TStoreProps>
>(
  component: React.ComponentType<TProps>
) => {
  return inject(...stores)(component) as InjectedComponent<TStoreProps, TProps>;
};

type InjectedComponent<TStoreProps extends keyof StoresType, TProps extends Pick<StoresType, TStoreProps>> = React.FC<
  Omit<TProps, keyof Pick<StoresType, TStoreProps>> & Partial<Pick<StoresType, TStoreProps>>
> &
  IWrappedComponent<TProps>;
